import React, { useEffect, useRef, useState } from "react";

/**
 * Streaming embed viewer — mirrors Sketchfab's loading behaviour:
 *
 *  1. Fetch tiny metadata JSON  →  thumbnail poster shows INSTANTLY
 *  2. Pass stream URL directly to model-viewer  →  it streams + renders progressively
 *     (no buffering the whole file in RAM before rendering starts)
 *  3. model-viewer fires progress events  →  we drive the progress bar from those
 *  4. model-viewer auto-decodes Draco + KTX2 as chunks arrive
 *
 *  WHY the file is 150 MB:
 *    The GLB on the backend is uncompressed. Draco compression must be applied
 *    at UPLOAD time (server-side). After Draco a 150 MB mesh typically becomes
 *    8–20 MB. See AddAssetModal — compression should be added there or in the
 *    backend upload handler using gltf-pipeline / draco3d.
 *
 *    npm install -g gltf-pipeline
 *    gltf-pipeline -i model.glb -o model-draco.glb --draco.compressionLevel 10
 *
 *  ── Backend requirements ────────────────────────────────────────────────
 *
 *  GET /api/v1/library/:id
 *    Existing endpoint. Must send CORS header:
 *      Access-Control-Allow-Origin: *   (or the embed page's origin)
 *    Returns: { data: { name, type, thumbnail, url, ... } }
 *
 *  GET /api/v1/library/:id/stream
 *    Streams raw file bytes. Required headers:
 *      Content-Type: model/gltf-binary (or appropriate mime)
 *      Content-Length: <bytes>           ← enables % progress bar
 *      Cache-Control: public, max-age=86400
 *      Access-Control-Allow-Origin: *    ← required for cross-origin iframe
 *    Auth: accept Bearer token via Authorization header OR ?token= query param.
 *
 *  Express + S3 example:
 *
 *    router.get('/library/:id/stream', optionalAuth, async (req, res) => {
 *      const asset = await Asset.findById(req.params.id);
 *      if (!asset) return res.status(404).end();
 *
 *      const head = await s3.headObject({ Bucket, Key: asset.s3Key }).promise();
 *      res.setHeader('Access-Control-Allow-Origin', '*');
 *      res.setHeader('Content-Type', head.ContentType || 'application/octet-stream');
 *      res.setHeader('Content-Length', head.ContentLength);
 *      res.setHeader('Cache-Control', 'public, max-age=86400');
 *      s3.getObject({ Bucket, Key: asset.s3Key }).createReadStream().pipe(res);
 *    });
 * ─────────────────────────────────────────────────────────────────────────
 */

const API_URL = process.env.REACT_APP_API_URL || "https://devserver.spatialgrid.ai";

function fmtBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function EmbedViewer() {
  const assetId = new URLSearchParams(window.location.search).get("id");

  // meta → streaming → ready | error
  const [phase, setPhase] = useState("meta");
  const [asset, setAsset] = useState(null);   // { name, type, thumbnail, streamUrl }
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const mvRef = useRef(null);

  // ── Step 1: fetch metadata ─────────────────────────────────────────────
  useEffect(() => {
    if (!assetId) {
      setErrorMsg("No asset ID provided.");
      setPhase("error");
      return;
    }

    fetch(`${API_URL}/api/v1/library/${assetId}`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (!res.ok)
          throw new Error(
            res.status === 401
              ? "Authentication required."
              : res.status === 403
              ? "Asset not publicly available."
              : `Server error (${res.status})`
          );
        return res.json();
      })
      .then((json) => {
        const meta = json.data ?? json;
        const name = meta.name || meta.title || "Asset";
        const type = meta.type || "3D";
        const thumbnail = meta.thumbnail || null;

        // Prefer the pre-compressed stream URL (Draco, 10-15x smaller).
        // Falls back to the original URL if no compressed version exists yet.
        // Original (full quality) is never used here — that's for downloads only.
        // Prefer pre-compressed streamUrl (Draco), fall back to /stream endpoint
        const streamUrl =
          meta.streamUrl ||
          `${API_URL}/api/v1/library/${assetId}/stream`;

        setAsset({ name, type, thumbnail, streamUrl });
        setPhase("streaming");
      })
      .catch((err) => {
        setErrorMsg(err.message || "Failed to load asset.");
        setPhase("error");
      });
  }, [assetId]);

  // ── Step 2: wire model-viewer progress / load / error events ──────────
  useEffect(() => {
    const el = mvRef.current;
    if (!el || phase !== "streaming") return;

    const onProgress = (e) => {
      // totalProgress is 0 → 1
      setProgress(Math.round((e.detail?.totalProgress ?? 0) * 100));
    };
    const onLoad = () => setPhase("ready");
    const onError = (e) => {
      setErrorMsg(e.detail?.message || "Failed to render model.");
      setPhase("error");
    };

    el.addEventListener("progress", onProgress);
    el.addEventListener("load", onLoad);
    el.addEventListener("error", onError);

    return () => {
      el.removeEventListener("progress", onProgress);
      el.removeEventListener("load", onLoad);
      el.removeEventListener("error", onError);
    };
  }, [phase, asset]);

  // ── Render ─────────────────────────────────────────────────────────────
  const name = asset?.name || "";
  const thumbnail = asset?.thumbnail;
  const streamUrl = asset?.streamUrl;
  const type = asset?.type;

  return (
    <>
      <style>{`
        @keyframes sg-spin { to { transform: rotate(360deg); } }
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 100%; height: 100%; overflow: hidden; background: #121316; }
        #root { width: 100%; height: 100%; }
        a { text-decoration: none; }
        a:hover { text-decoration: underline; }
      `}</style>

      <div style={s.root}>
        <div style={s.viewerArea}>

          {/* ── model-viewer: rendered once asset is known, hidden until ready ── */}
          {(phase === "streaming" || phase === "ready") && type === "3D" && (
            <model-viewer
              ref={mvRef}
              alt={name}
              src={streamUrl}
              poster={thumbnail ?? undefined}
              loading="eager"
              reveal="auto"
              camera-controls
              ar
              xr-spatial-tracking
              shadow-intensity="0.7"
              shadow-softness="0.5"
              exposure="1"
              tone-mapping="commerce"
              style={{ width: "100%", height: "100%" }}
            />
          )}

          {/* image */}
          {(phase === "streaming" || phase === "ready") && type === "image" && (
            <img src={streamUrl} alt={name} style={s.imageFit} onLoad={() => setPhase("ready")} />
          )}

          {/* video */}
          {(phase === "streaming" || phase === "ready") && type === "video" && (
            <video src={streamUrl} controls autoPlay muted style={s.videoFit} onCanPlay={() => setPhase("ready")} />
          )}

          {/* ── Overlay: shown while metadata loads OR while model streams ── */}
          {phase !== "ready" && phase !== "error" && (
            <div style={s.overlay}>
              {/* Blurred thumbnail behind spinner — visible instantly */}
              {thumbnail && (
                <img src={thumbnail} alt="" style={s.posterBg} aria-hidden="true" />
              )}

              <div style={s.overlayContent}>
                {phase === "meta" ? (
                  <>
                    <div style={s.spinner} />
                    <p style={s.overlayLabel}>Loading…</p>
                  </>
                ) : (
                  <>
                    {/* While streaming model-viewer takes over rendering,
                        so we only show this overlay until first model-viewer
                        progress fires — then it fades out automatically. */}
                    {progress < 5 && (
                      <>
                        <div style={s.spinner} />
                        <p style={s.overlayLabel}>Preparing stream…</p>
                      </>
                    )}
                    {progress >= 5 && (
                      <>
                        <p style={s.overlayLabel}>
                          {progress < 100
                            ? `Loading… ${progress}%`
                            : "Rendering…"}
                        </p>
                        <div style={s.barTrack}>
                          <div
                            style={{
                              ...s.barFill,
                              width: `${progress}%`,
                              transition: "width 0.2s ease",
                            }}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Error ── */}
          {phase === "error" && (
            <div style={s.overlay}>
              <div style={s.overlayContent}>
                <p style={{ ...s.overlayLabel, color: "#EE2B2A", fontSize: 13 }}>
                  {errorMsg}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={s.footer}>
          <span style={s.footerName}>{name}</span>
          <span style={s.dot}>·</span>
          <span style={s.footerLabel}>Powered by</span>
          <a
            href="https://spatialgrid.com"
            target="_blank"
            rel="noopener noreferrer"
            style={s.footerLink}
          >
            Spatialgrid
          </a>
        </div>
      </div>
    </>
  );
}

const s = {
  root: {
    width: "100%", height: "100vh",
    display: "flex", flexDirection: "column",
    background: "#121316",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  viewerArea: {
    flex: 1, position: "relative", overflow: "hidden",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  overlay: {
    position: "absolute", inset: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(18,19,22,0.75)",
    zIndex: 10,
  },
  posterBg: {
    position: "absolute", inset: 0,
    width: "100%", height: "100%",
    objectFit: "cover", opacity: 0.22, filter: "blur(14px)",
  },
  overlayContent: {
    position: "relative", zIndex: 1,
    display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
  },
  spinner: {
    width: 38, height: 38,
    border: "3px solid #2d2e32", borderTopColor: "#EE2B2A",
    borderRadius: "50%", animation: "sg-spin 0.75s linear infinite",
  },
  overlayLabel: { color: "#aaa", fontSize: 12, textAlign: "center" },
  barTrack: {
    width: 160, height: 3,
    background: "#2d2e32", borderRadius: 99, overflow: "hidden",
  },
  barFill: { height: "100%", background: "#EE2B2A", borderRadius: 99 },
  imageFit: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
  videoFit: { maxWidth: "100%", maxHeight: "100%" },
  footer: {
    padding: "6px 14px", background: "#1A1B1E",
    borderTop: "1px solid #2d2e32",
    display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
  },
  footerName: { fontSize: 11, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 },
  dot: { color: "#444", fontSize: 11 },
  footerLabel: { fontSize: 11, color: "#555" },
  footerLink: { fontSize: 11, color: "#EE2B2A", fontWeight: 600 },
};
